import { useEffect } from 'react';
import styled from 'styled-components';
import { withArticle } from './corrections';

/**
 * What can be done to the object a person clicked on the video (design: corrections_design.html, section 2): remove
 * it, remove it and everything like it ("not a car"), merge it with another track that is the same object, or take
 * back an object they added. Sits next to the dot, inside the picture; Escape closes it, Delete removes.
 */

const tk = ({ theme: t }) => t.token;

const Card = styled.div`
	position: absolute;
	z-index: 6;
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).lineWidthBold}px;
	min-width: ${p => tk(p).controlHeight * 6}px;
	padding: ${p => tk(p).paddingXXS}px;
	border-radius: ${p => tk(p).borderRadiusLG}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	box-shadow: ${p => tk(p).boxShadowSecondary};
	color: ${p => tk(p).colorTextLightSolid};
	font-size: ${p => tk(p).fontSizeXS}px;
`;

const Head = styled.div`
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingXS}px`};
	margin-bottom: ${p => tk(p).lineWidthBold}px;
	border-bottom: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	color: ${p => tk(p).colorTextTertiary};
	font-size: ${p => tk(p).fontSizeXXS}px;
	white-space: nowrap;
`;

const Item = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => tk(p).marginSM}px;
	width: 100%;
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingXS}px`};
	border: 0;
	border-radius: ${p => tk(p).borderRadius}px;
	background: transparent;
	color: ${p => (p.$danger ? tk(p).colorErrorHover : 'inherit')};
	font: inherit;
	text-align: left;
	cursor: pointer;
	&:hover,
	&:focus-visible {
		background: ${p => tk(p).colorFillTertiary};
		outline: none;
	}
	kbd {
		background: transparent;
		color: ${p => tk(p).colorTextTertiary};
		font-size: ${p => tk(p).fontSizeXXS}px;
		line-height: 1;
		border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
		border-radius: ${p => tk(p).borderRadiusXS}px;
		padding: ${p => `${tk(p).lineWidthBold}px ${tk(p).paddingXXS}px`};
	}
`;

const Hint = styled.div`
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingXS}px`};
	color: ${p => tk(p).colorText};
	line-height: ${p => tk(p).lineHeight};
`;

const WIDTH = 210;
const HEIGHT = 170;

const PickMenu = ({
	pick,
	subject = 'object',
	// "#12 · 0:27–0:33": which track and when it is in view, for the header
	about = '',
	added = false,
	merging = false,
	onRemove,
	onNot,
	onMerge,
	onUndoAdd,
	onClose,
}) => {
	useEffect(() => {
		// only while a dot is picked, and never while typing (the assistant's text box is on the page too)
		if (!pick) return undefined;
		const onKey = e => {
			if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
			if (e.key === 'Escape') onClose();
			else if ((e.key === 'Delete' || e.key === 'Backspace') && !merging) {
				e.preventDefault();
				if (added) onUndoAdd();
				else onRemove();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [pick, added, merging, onClose, onRemove, onUndoAdd]);
	if (!pick) return null;
	// next to the dot, kept inside the picture
	const style = {
		left: Math.max(8, pick.x + 16 + WIDTH > pick.width ? pick.x - 16 - WIDTH : pick.x + 16),
		top: Math.max(8, Math.min(pick.y - 20, pick.height - HEIGHT - 8)),
	};
	return (
		<Card
			style={style}
			role="dialog"
			aria-label={`Correct this ${subject}`}
			onPointerDown={e => e.stopPropagation()}>
			<Head>
				{merging
					? 'Merge'
					: `${added ? 'Added by you' : 'Track'} · ${subject}${about ? ` · ${about}` : ''}`}
			</Head>
			{merging ? (
				<Hint>Click the other dot that is the same {subject}.</Hint>
			) : added ? (
				<>
					<Item type="button" onClick={onUndoAdd} autoFocus>
						Undo this addition <kbd>Del</kbd>
					</Item>
					<Item type="button" onClick={onMerge}>
						Same as another…
					</Item>
				</>
			) : (
				<>
					<Item type="button" $danger onClick={onRemove} autoFocus>
						Remove this object <kbd>Del</kbd>
					</Item>
					<Item type="button" $danger onClick={onNot}>
						Not {withArticle(subject)} — remove all like it
					</Item>
					<Item type="button" onClick={onMerge}>
						Same as another…
					</Item>
				</>
			)}
			<Item type="button" onClick={onClose}>
				Cancel <kbd>Esc</kbd>
			</Item>
		</Card>
	);
};

export default PickMenu;
